import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import SearchActionChip from '../components/search/SearchActionChip';
import { useNavigate, useSearchParams } from 'react-router-dom';
import ListPageLayout from '../components/hand/ListPageLayout';
import { TintSquare } from '../components/hand';
import { FriendCard } from '../components/FriendsDirectory';
import { SuggestionCard } from '../components/PeopleYouMayKnow';
import { SectionHeading, usePeopleToast, errorMessage } from '../components/people/PeopleUI';
import friendService from '../services/friendService';
import WallEventList from '../components/wall/WallEventList';
import { prepareEventPosters } from '../components/wall/preparePosters';
import useMyPortalSources from '../hooks/useMyPortalSources';
import MobileEventSheet from '../components/MobileEventSheet';
import useMediaQuery from '../utils/useMediaQuery';
import { searchService, featuredWallService } from '../services';
import useScrollRestore from '../hooks/useScrollRestore';
import { searchKey, getListCache, saveListCache } from '../utils/listCache';

/**
 * /search — the full results page behind the dropdown's VIEW ALL footers.
 * Every category (events, people, clubs) infinite-scrolls, and the filter-chip
 * counts come from the same search payload the dropdown uses, so they always
 * agree. docs/plans/2026-08-handoff-layout/08-search.md
 *
 * The EVENTS view is the events wall's own body: WallEventCards in masonry columns
 * (WallEventList) in its masonry grid, posters prepared before a page lands so
 * the columns never jump, skeletons over the old list while a new query loads, and on
 * a phone a tapped card opens the event sheet on a `?event=CODE` history entry — the
 * same card and the same flow as /featured, so a search never lands on a different
 * page design.
 *
 * The PEOPLE view is the People page's cards: friends as FriendCards (YOUR FRIENDS —
 * ring, status line, RESUME · MESSAGE · portal) and everyone else as SuggestionCards
 * (MORE PEOPLE — photo, + ADD in place, Pro pages say VIEW PAGE), grouped that way as
 * pages load. Clubs stay rows.
 */
const VIEWS = [
  { key: 'events', label: 'Events' },
  { key: 'people', label: 'People' },
  { key: 'clubs', label: 'Clubs' },
];

const PAGE = 24;

const LOCATION_KEY = 'pr.userLocation';
function readLocation() {
  try {
    const raw = localStorage.getItem(LOCATION_KEY);
    return raw ? JSON.parse(raw) : null;
  } catch { return null; }
}

export default function SearchResultsPage() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const q = searchParams.get('q') || '';
  const view = VIEWS.some((v) => v.key === searchParams.get('view')) ? searchParams.get('view') : 'events';
  const location = useMemo(readLocation, []);
  const lat = location?.lat ?? null;
  const lng = location?.lng ?? null;

  // The wall's card chrome: the OPEN A PORTAL ring and the phone event sheet.
  const portalFor = useMyPortalSources();
  const isMobile = useMediaQuery('(max-width: 767px)');
  const openEventCode = searchParams.get('event');
  const handleEventOpen = useCallback((ev) => {
    const code = ev?.inviteCode;
    if (!code) return;
    if (isMobile) {
      setSearchParams((prev) => { const next = new URLSearchParams(prev); next.set('event', code); return next; });
    } else {
      navigate(`/events/${code}`);
    }
  }, [isMobile, navigate, setSearchParams]);
  const closeEventSheet = useCallback(() => {
    const idx = window.history.state && typeof window.history.state.idx === 'number' ? window.history.state.idx : 0;
    if (idx > 0) navigate(-1);
    else setSearchParams((prev) => { const next = new URLSearchParams(prev); next.delete('event'); return next; });
  }, [navigate, setSearchParams]);

  // Browse payload — drives the filter-chip counts (matching the dropdown) and is
  // the fallback list if a paged endpoint isn't reachable.
  const [payload, setPayload] = useState(null);
  const payloadRef = useRef(null);

  // PEOPLE: + ADD sends the request in place (the People page's flow) and says so.
  const [notify, toast] = usePeopleToast();
  const [sentIds, setSentIds] = useState(() => new Set());
  const [adding, setAdding] = useState(() => new Set());
  const addPerson = useCallback(async (person) => {
    if (person.relationshipStatus === 'request_received') { navigate(person.href); return; }
    if (adding.has(person.userId) || sentIds.has(person.userId)) return;
    setAdding((prev) => new Set(prev).add(person.userId));
    try {
      const result = await friendService.sendFriendRequest(person.userId);
      if (!result.success && !/already/i.test(result.error || '')) throw new Error(errorMessage(result, 'Could not send request.'));
      setSentIds((prev) => new Set(prev).add(person.userId));
      notify(`Request sent to ${person.displayName}`);
    } catch (e) {
      notify(e.message);
    } finally {
      setAdding((prev) => { const next = new Set(prev); next.delete(person.userId); return next; });
    }
  }, [adding, sentIds, notify, navigate]);

  // Tap a result, hit back, land where you were. The list is keyed by view+query
  // so each tab keeps its own place.
  const restoreKey = searchKey(view, q);
  const { cameFromBack } = useScrollRestore(restoreKey);

  // On back, seed from the cache so the FIRST paint already has every page the
  // user had scrolled through — otherwise the list is 24 rows tall and there is
  // nowhere for the restored offset to go.
  const seed = cameFromBack ? getListCache(restoreKey) : null;
  const seededKey = useRef(seed ? restoreKey : null);

  // Active view's list (paged).
  const [list, setList] = useState(seed?.items || []);
  const [skip, setSkip] = useState(seed?.skip || 0);
  const [hasMore, setHasMore] = useState(seed?.hasMore ?? false);
  const [loading, setLoading] = useState(!seed);
  const [loadingMore, setLoadingMore] = useState(false);
  const pagedMode = useRef(true);
  const sentinelRef = useRef(null);

  // Counts payload (once per query).
  useEffect(() => {
    let cancelled = false;
    (async () => {
      const r = await searchService.search({ q, lat, lng });
      if (cancelled) return;
      if (r.success) { setPayload(r.data); payloadRef.current = r.data; }
    })();
    return () => { cancelled = true; };
  }, [q, lat, lng]);

  // Fetch one page for the active view. Events come from the wall (full cards, posters
  // prepared first so the masonry lays them out once); people/clubs from the paged
  // search endpoints (rows).
  const fetchPage = useCallback(async (skipVal) => {
    if (view === 'events') {
      const r = await featuredWallService.getEvents({ lat, lng, search: q || null, skip: skipVal, take: PAGE });
      if (!r.success || !r.data) return { ok: false, items: [], more: false };
      return { ok: true, items: await prepareEventPosters(r.data.events || []), more: !!r.data.hasMore };
    }
    const fetcher = view === 'people' ? searchService.searchPeople : searchService.searchClubs;
    const r = await fetcher({ q, skip: skipVal, take: PAGE });
    if (!r.success || !r.data) return { ok: false, items: [], more: false };
    const items = r.data.items || [];
    const tot = r.data.total ?? items.length;
    return { ok: true, items, more: items.length > 0 && skipVal + items.length < tot };
  }, [view, q, lat, lng]);

  // Page 1 whenever the view / query changes — except the one mount where we
  // just restored this exact list, which would otherwise be thrown away and
  // refetched as a single page, undoing the restore.
  useEffect(() => {
    if (seededKey.current === restoreKey) { seededKey.current = null; return undefined; }
    let cancelled = false;
    (async () => {
      setLoading(true); setList([]); setSkip(0); setHasMore(false);
      const r = await fetchPage(0);
      if (cancelled) return;
      if (r.ok) {
        pagedMode.current = true;
        setList(r.items);
        setSkip(r.items.length);
        setHasMore(r.more);
        saveListCache(restoreKey, { items: r.items, skip: r.items.length, hasMore: r.more });
      } else if (view !== 'events') {
        // Paged endpoint unavailable → show the browse payload, no infinite scroll.
        pagedMode.current = false;
        const fb = view === 'people' ? payloadRef.current?.people : payloadRef.current?.clubs;
        setList(fb?.items || []);
        setHasMore(false);
      } else {
        setList([]);
        setHasMore(false);
      }
      setLoading(false);
    })();
    return () => { cancelled = true; };
  }, [fetchPage, view, restoreKey]);

  const loadMore = useCallback(async () => {
    if (loadingMore || !hasMore || !pagedMode.current) return;
    setLoadingMore(true);
    const r = await fetchPage(skip);
    if (r.ok) {
      setList((prev) => {
        const next = [...prev, ...r.items];
        saveListCache(restoreKey, { items: next, skip: skip + r.items.length, hasMore: r.more });
        return next;
      });
      setSkip(skip + r.items.length);
      setHasMore(r.more);
    } else {
      setHasMore(false);
    }
    setLoadingMore(false);
  }, [loadingMore, hasMore, fetchPage, skip, restoreKey]);

  // Infinite-scroll: observe the layout's sentinel (600px early, like the wall).
  useEffect(() => {
    const el = sentinelRef.current;
    if (!el || typeof IntersectionObserver === 'undefined') return undefined;
    const obs = new IntersectionObserver((entries) => {
      if (entries[0]?.isIntersecting) loadMore();
    }, { rootMargin: '600px' });
    obs.observe(el);
    return () => obs.disconnect();
  }, [loadMore]);

  const setView = (key) => {
    const next = new URLSearchParams(searchParams);
    next.set('view', key);
    setSearchParams(next);
  };

  // Chip counts — all from the search payload, so they match the dropdown exactly.
  const counts = {
    events: payload?.events?.total || 0,
    people: payload?.people?.total || 0,
    clubs: payload?.clubs?.total || 0,
  };

  const filters = (
    <>
      {VIEWS.map((v) => (
        <button
          key={v.key}
          type="button"
          onClick={() => setView(v.key)}
          className={`flex h-[38px] flex-shrink-0 items-center border-2 border-black px-3.5 font-mono text-[12px] uppercase tracking-[0.04em] transition-colors ${
            view === v.key ? 'bg-black text-white' : 'bg-white text-black hover:bg-black hover:text-white'
          }`}
        >
          {v.label} · {counts[v.key]}
        </button>
      ))}
    </>
  );

  const total = counts[view];
  const showLoading = loading || loadingMore;
  const isEmpty = !showLoading && list.length === 0;
  const empty = (
    <div className="flex flex-col items-center gap-1.5 py-16">
      <span className="font-mono text-[12px] font-bold uppercase tracking-[0.14em] text-black">No matches.</span>
      <span className="font-mono text-[12px] text-hand-muted">Try a venue, a first name, or a club.</span>
    </div>
  );

  return (
    <>
      <ListPageLayout
        eyebrow={q ? `Results for “${q}”` : 'Search'}
        title={q || 'Search'}
        subtitle="Events, people and clubs near you."
        active="events"
        filters={filters}
        sentinelRef={sentinelRef}
        shownLabel={list.length ? `Showing ${list.length}${total ? ` of ${total}` : ''}` : ''}
        loading={showLoading}
        hasMore={hasMore}
        isEmpty={isEmpty}
        layout={view === 'clubs' ? 'cards' : 'bare'}
        empty={empty}
      >
        {view === 'events' && (
          // A new query → skeletons over the old list (the page never collapses); the next
          // page → a few skeleton cards under it.
          <WallEventList
            events={list}
            viewMode="grid"
            resetKey={restoreKey}
            refreshing={loading}
            fetching={loadingMore}
            onEventOpen={handleEventOpen}
            portalFor={portalFor}
          />
        )}

        {view === 'people' && (
          <PeopleResults
            items={list}
            sentIds={sentIds}
            adding={adding}
            onAdd={addPerson}
            onNavigate={navigate}
          />
        )}

        {view === 'clubs' && list.map((item) => <ResultRow key={item.id} item={item} onNavigate={navigate} />)}
      </ListPageLayout>
      {toast}

      {/* Phone: a tapped card opens the event as a sheet over the results, like the wall. */}
      <MobileEventSheet code={openEventCode} open={isMobile && !!openEventCode} onClose={closeEventSheet} />
    </>
  );
}

/** A search person item → the shape the People page's cards read. */
export function toPerson(item, sentIds) {
  const meta = String(item.meta || '');
  const handle = item.handle || (meta.match(/@([^\s·]+)/) || [])[1] || null;
  const location = item.location ?? (meta.split(' · ').filter((s) => !s.startsWith('@'))[0] || null);
  return {
    userId: item.id,
    displayName: item.title,
    handle,
    location,
    avatarUrl: item.imageUrl || null,
    href: item.href,
    isPro: item.tag === 'PRO',
    // MESSAGE is the chip a friend's row gets — the one signal every server version sends.
    isFriend: !!item.isFriend || item.relationshipStatus === 'friends' || item.action === 'MESSAGE',
    isClose: !!item.isClose,
    relationshipStatus: item.relationshipStatus || (item.isFriend || item.action === 'MESSAGE' ? 'friends' : item.action === 'PENDING' ? 'request_sent' : 'none'),
    requestSent: item.relationshipStatus === 'request_sent' || item.action === 'PENDING' || !!(sentIds && sentIds.has(item.id)),
    upcomingEventCount: item.upcomingEventCount || 0,
    livePortalCount: item.livePortalCount || 0,
    activeSignalExpiresAt: item.activeSignalExpiresAt || null,
  };
}

/**
 * PEOPLE results laid out the way the People page lays out people: YOUR FRIENDS as
 * FriendCards, then MORE PEOPLE as SuggestionCards. Grouped from whatever has loaded.
 */
function PeopleResults({ items, sentIds, adding, onAdd, onNavigate }) {
  const people = items.map((it) => toPerson(it, sentIds));
  const friends = people.filter((p) => p.isFriend);
  const others = people.filter((p) => !p.isFriend);
  return (
    <div className="people-scope flex flex-col" style={{ gap: 32 }} data-testid="people-results">
      {friends.length > 0 && (
        <section data-testid="people-friends">
          <SectionHeading detail={String(friends.length)}>Your friends</SectionHeading>
          <div className="people-friends">
            {friends.map((person) => (
              <FriendCard
                key={person.userId}
                person={person}
                onOpen={() => onNavigate(person.href)}
                onMessage={() => onNavigate(`/messages?with=${person.userId}`)}
                onPortal={() => onNavigate('/portals/new')}
              />
            ))}
          </div>
        </section>
      )}
      {others.length > 0 && (
        <section data-testid="people-more">
          <SectionHeading detail={String(others.length)}>{friends.length ? 'More people' : 'People'}</SectionHeading>
          <div className="people-suggestions">
            {others.map((person) => (
              <SuggestionCard
                key={person.userId}
                person={person}
                busy={adding.has(person.userId)}
                onOpen={() => onNavigate(person.href)}
                onAdd={() => (person.isPro ? onNavigate(person.href) : onAdd(person))}
                actionLabel={person.isPro ? 'View page' : null}
              />
            ))}
          </div>
        </section>
      )}
    </div>
  );
}

/** A club row: square, name, member count, the action chip. */
function ResultRow({ item, onNavigate }) {
  return (
    <div className="relative flex items-stretch overflow-hidden border-2 border-black bg-white">
      <button
        type="button"
        onClick={() => onNavigate(item.href)}
        className="flex min-w-0 flex-1 items-stretch text-left"
      >
        <TintSquare name={item.title} imageUrl={item.imageUrl} size={84} tintKey={item.id} className="border-0 border-r-2" />
        <span className="flex min-w-0 flex-1 flex-col justify-center gap-1 px-4 py-3.5">
          <span className="truncate text-[18px] text-black">{item.title}</span>
          <span className="truncate font-mono text-[11px] uppercase tracking-[0.06em] text-hand-muted">{item.meta}</span>
        </span>
      </button>
      <span className="flex flex-shrink-0 items-center gap-2 pl-2 pr-4">
        <SearchActionChip item={item} onNavigate={onNavigate} />
      </span>
    </div>
  );
}
